import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaFileContract, FaCheckCircle, FaDollarSign, FaClock, FaHandshake, FaUsers } from "react-icons/fa";
import "./Statistics.css";

const Statistics = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const stats = [
    { key: "activePolicies", value: 6500, suffix: "+", Icon: FaFileContract },
    { key: "claimsSettled", value: 10800, suffix: "+", Icon: FaCheckCircle },
    { key: "premiumVolume", value: 1.7, suffix: "M", Icon: FaDollarSign },
    { key: "yearsExperience", value: 30, suffix: "+", Icon: FaClock },
    { key: "insurancePartners", value: 12, suffix: "+", Icon: FaHandshake },
    { key: "satisfiedClients", value: 18000, suffix: "+", Icon: FaUsers },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            // Add show class to cards
            const cards = entry.target.querySelectorAll(".statistic-card.hidden");
            cards.forEach((card) => {
              card.classList.add("show");
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    const current = sectionRef.current;
    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, []);

  const AnimatedNumber = ({ end, suffix, duration = 2000, delay = 0, visible }) => {
    const [count, setCount] = useState(0);
    const countRef = useRef(0);
    const hasDecimal = Number(end) % 1 !== 0;

    useEffect(() => {
      if (!visible) return;

      const timer = setTimeout(() => {
        const startTime = Date.now();
        const startValue = 0;

        const animate = () => {
          const now = Date.now();
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Easing function for smooth animation
          const easeOutQuart = 1 - Math.pow(1 - progress, 4);
          const currentRaw = startValue + (end - startValue) * easeOutQuart;
          const current = hasDecimal ? Number(currentRaw.toFixed(1)) : Math.floor(currentRaw);

          if (current !== countRef.current) {
            countRef.current = current;
            setCount(current);
          }

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            setCount(end);
          }
        };

        requestAnimationFrame(animate);
      }, delay);

      return () => clearTimeout(timer);
    }, [visible, end, duration, delay, hasDecimal]);

    return (
      <span className="stat-number">
        {count.toLocaleString(undefined, hasDecimal ? { minimumFractionDigits: 1, maximumFractionDigits: 1 } : undefined)}
        {suffix && <span className="stat-suffix">{suffix}</span>}
      </span>
    );
  };

  return (
    <section className="statistics-section" ref={sectionRef}>
      <div className="container">
        <h2 className="statistics-title">{t("home.statistics.title")}</h2>
        <div className="statistics-grid">
          {stats.map((stat, i) => (
            <div
              key={stat.key}
              className="statistic-card hidden"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="statistic-icon">
                <div className="icon-circle">
                  <stat.Icon aria-hidden="true" />
                </div>
              </div>
              <AnimatedNumber
                end={stat.value}
                suffix={stat.suffix}
                duration={2000}
                delay={i * 150}
                visible={isVisible}
              />
              <h3 className="statistic-label">
                {t(`home.statistics.${stat.key}`)}
              </h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Statistics;

